import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { createCheckoutSession } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, ChevronRight, Loader2, X, Building2, Infinity } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { Seo } from '@/components/Seo';
import { toast } from 'sonner';

const plans = [
  {
    id: 'professional', name: 'Professional', price: 249, perVendor: 4.50,
    desc: 'For growing property management companies',
    highlights: [
      { text: 'Up to 5 properties', icon: 'building', bold: true },
      { text: 'Vendor-Paid billing not available', icon: 'x', muted: true },
      { text: 'PM pays $4.50/vendor/mo', icon: 'check' },
      { text: 'All features included', icon: 'check' },
    ],
  },
  {
    id: 'enterprise', name: 'Enterprise', price: 599, perVendor: 3.50, popular: true,
    desc: 'For large-scale property management operations',
    badgeText: 'Best Value',
    highlights: [
      { text: 'Unlimited properties', icon: 'infinity', bold: true },
      { text: 'Vendor-Paid option — vendors pay $50/yr', icon: 'check', accent: true },
      { text: 'Lower rate: $3.50/vendor/mo', icon: 'check' },
      { text: 'All features included', icon: 'check' },
    ],
  }
];

export default function PricingPage() {
  const { user, isSubscriptionActive, logout } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  useEffect(() => {
    // Paid (professional/enterprise) active subscribers don't need the pricing page,
    // but limited_demo users must be able to view it to upgrade.
    if (isSubscriptionActive && user?.plan_tier !== 'limited_demo') {
      navigate('/dashboard');
    }
  }, [isSubscriptionActive, user, navigate]);

  const handleSelectPlan = (planId) => {
    if (!user) {
      navigate('/signup');
      return;
    }
    // Show terms dialog before checkout
    setSelectedPlan(planId);
    setShowTermsDialog(true);
  };

  const handleProceedToCheckout = async () => {
    if (!agreedToTerms || !agreedToPrivacy) {
      toast.error('Please agree to both Terms of Service and Privacy Policy');
      return;
    }
    
    setLoadingPlan(selectedPlan);
    setShowTermsDialog(false);
    
    try {
      const originUrl = window.location.origin;
      const { data } = await createCheckoutSession({ plan_id: selectedPlan, origin_url: originUrl });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoadingPlan(null);
      setAgreedToTerms(false);
      setAgreedToPrivacy(false);
    }
  };

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-background">
      <Seo
        title="Pricing — Vendor Compliance Automation Plans"
        description="VendorSuite pricing: Professional from $249/mo + $4.50 per vendor, Enterprise from $599/mo + $3.50 per vendor with unlimited properties. 30-day free trial, cancel anytime."
        path="/pricing"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'VendorSuite',
          description: 'AI-powered vendor compliance automation for property managers.',
          offers: [
            { '@type': 'Offer', name: 'Professional', price: '249', priceCurrency: 'USD', description: '$249/month base + $4.50 per vendor. Up to 5 properties.' },
            { '@type': 'Offer', name: 'Enterprise', price: '599', priceCurrency: 'USD', description: '$599/month base + $3.50 per vendor. Unlimited properties.' },
          ],
        }}
      />
      {/* Nav */}
      <nav className="border-b border-border bg-background/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo width={180} />
          </div>
          <div className="flex items-center gap-3">
            {user && <span className="text-sm text-muted-foreground">{user.email}</span>}
            {user && <Button variant="ghost" size="sm" onClick={logout}>Sign out</Button>}
            {!user && <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>Log in</Button>}
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Choose your plan</h1>
          <p className="text-muted-foreground">Subscribe to start automating vendor compliance. Cancel anytime.</p>
          {user && user.subscription_status === 'inactive' && (
            <Badge variant="secondary" className="mt-4">Complete payment to access your account</Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <Card key={plan.id} className={`rounded-xl border shadow-[var(--shadow-soft)] relative ${plan.popular ? 'border-primary shadow-[var(--shadow-pop)]' : ''}`}>
              {plan.popular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">{plan.badgeText || 'Best Value'}</Badge>}
              <CardHeader className="pb-4">
                <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{plan.name}</CardTitle>
                <CardDescription>{plan.desc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>${plan.price}</span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">+ ${plan.perVendor.toFixed(2)}/active vendor/mo</p>
                </div>
                <Button className="w-full" variant={plan.popular ? 'default' : 'outline'}
                  data-testid={`checkout-${plan.id}-button`}
                  disabled={loadingPlan === plan.id}
                  onClick={() => handleSelectPlan(plan.id)}
                >
                  {loadingPlan === plan.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {loadingPlan === plan.id ? 'Redirecting to checkout...' : 'Subscribe Now'}
                  {loadingPlan !== plan.id && <ChevronRight className="ml-1 h-4 w-4" />}
                </Button>
                <div className="space-y-3">
                  {plan.highlights.map((h, j) => {
                    const iconMap = { check: CheckCircle2, x: X, building: Building2, infinity: Infinity };
                    const Icon = iconMap[h.icon] || CheckCircle2;
                    return (
                      <div key={j} className={`flex items-start gap-2 text-sm ${h.muted ? 'text-muted-foreground' : ''}`}>
                        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${h.muted ? 'text-muted-foreground/50' : h.accent ? 'text-[hsl(142,56%,36%)]' : 'text-primary'}`} />
                        <span className={h.bold ? 'font-semibold' : ''}>{h.text}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8 max-w-lg mx-auto">Both plans include every feature: AI extraction, compliance scoring, automated chasing, vendor portal, reporting, bulk import, and more. The only differences are property limits and billing flexibility.</p>
      </div>

      {/* Terms & Privacy Agreement Dialog */}
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Before You Subscribe</DialogTitle>
            <DialogDescription>
              Please review and agree to our legal terms before proceeding to payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <Checkbox 
                  id="terms" 
                  checked={agreedToTerms} 
                  onCheckedChange={setAgreedToTerms}
                  data-testid="pricing-terms-checkbox"
                />
                <label htmlFor="terms" className="text-sm cursor-pointer flex-1">
                  I have read and agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium">
                    Terms of Service
                  </Link>
                </label>
              </div>
              
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <Checkbox 
                  id="privacy" 
                  checked={agreedToPrivacy} 
                  onCheckedChange={setAgreedToPrivacy}
                  data-testid="pricing-privacy-checkbox"
                />
                <label htmlFor="privacy" className="text-sm cursor-pointer flex-1">
                  I have read and agree to the{' '}
                  <Link to="/privacy" target="_blank" className="text-primary hover:underline font-medium">
                    Privacy Policy
                  </Link>
                </label>
              </div>
            </div>

            {selectedPlan && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="font-medium mb-1">Selected Plan: {plans.find(p => p.id === selectedPlan)?.name}</p>
                <p className="text-muted-foreground text-xs">
                  ${plans.find(p => p.id === selectedPlan)?.price}/month + ${plans.find(p => p.id === selectedPlan)?.perVendor}/active vendor/month
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTermsDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleProceedToCheckout} 
              disabled={!agreedToTerms || !agreedToPrivacy || loadingPlan}
              data-testid="pricing-proceed-checkout"
            >
              {loadingPlan ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {loadingPlan ? 'Redirecting...' : 'Continue to Checkout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
